import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiService, Article } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import colors from '../theme/colors';

const SearchContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${colors.lightGray};
  min-height: calc(100vh - 200px);
`;

const PageTitle = styled.h1`
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  margin: 0 0 0.5rem 0;
  color: ${colors.mainBlack};
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
  background: ${colors.mainGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchForm = styled.form`
  margin-bottom: 2rem;
  max-width: 560px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid ${colors.goldGrey};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${colors.mainOrange};
    box-shadow: 0 0 0 3px rgba(255, 86, 30, 0.15);
  }

  &::placeholder {
    color: ${colors.textGray};
  }
`;

const SearchHint = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.9375rem;
  color: ${colors.textGray};
  line-height: 1.5;
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${colors.textGray};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${colors.textGray};
  font-size: 1.05rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.mainOrange};
  background-color: rgba(255, 86, 30, 0.08);
  border: 1px solid ${colors.mainOrange};
  border-radius: 8px;
  margin: 1rem 0;
`;

const DEBOUNCE_MS = 350;

const Search: React.FC = () => {
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const runSearch = useCallback(async (searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setArticles([]);
      setSearched(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.searchArticlesByTitle(trimmed);
      let list = response.data || [];
      const words = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
      if (words.length > 1) {
        list = list.filter((a) => {
          const title = (a.title || '').toLowerCase();
          return words.every((w) => title.includes(w));
        });
      }
      setArticles(Array.isArray(list) ? list : []);
    } catch (err) {
      setError('Не вдалося виконати пошук. Перевірте підключення до сервера.');
      setArticles([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setArticles([]);
      setSearched(false);
      return;
    }
    const t = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(inputValue.trim());
    if (inputValue.trim()) runSearch(inputValue.trim());
  };

  return (
    <SearchContainer>
      <PageTitle>Пошук статей</PageTitle>
      <SearchForm onSubmit={handleSubmit}>
        <SearchInput
          type="search"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setQuery(e.target.value);
          }}
          onFocus={() => setInputValue((v) => v)}
          placeholder="Введіть назву статті..."
          autoFocus
          aria-label="Пошук за назвою статті"
          autoComplete="off"
        />
        <SearchHint>Пошук лише за назвою. Результати оновлюються автоматично.</SearchHint>
      </SearchForm>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && <LoadingMessage>Пошук...</LoadingMessage>}

      {!loading && !error && searched && (
        <>
          {articles.length === 0 ? (
            <EmptyState>
              {query.trim() ? 'За вашим запитом нічого не знайдено.' : 'Введіть запит у поле вище.'}
            </EmptyState>
          ) : (
            <ArticlesGrid>
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </ArticlesGrid>
          )}
        </>
      )}
    </SearchContainer>
  );
};

export default Search;
